import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '../../shared/components/Box/Box';
import Button from '../../shared/components/Button/Button';
import Chip from '../../shared/components/Chip/Chip';
import DataGrid from '../../shared/components/DataGrid/DataGrid';
import Dialog from '../../shared/components/Dialog/Dialog';
import Input from '../../shared/components/Input/Input';
import Menu, { MenuItem } from '../../shared/components/Menu/Menu';
import Paper from '../../shared/components/Paper/Paper';
import Select from '../../shared/components/Select/Select';
import Typography from '../../shared/components/Typography/Typography';
import { stockApi } from './stock.api';
import './stockPage.scss';

const emptyItem = {
  sku: '', name: '', type: 'Matériel', category: '', unit: 'U', quantity: 0, reserved: 0,
  thresholdQuantity: 0, location: '', owner: '', purchasePrice: 0, sellingPrice: 0, description: '', status: 'Disponible',
};

const emptyMovement = {
  stockItemId: '', movement: 'Entrée', quantity: 1, movementDate: new Date().toISOString().slice(0, 10),
  reason: '', referenceType: '', referenceId: '', unitPrice: '',
};

const NEED_STATUSES = ['À traiter', 'À commander', 'Commandé', 'Réceptionné', 'Disponible', 'Réservé', 'Traité'];
const PRE_RECEIPT_STATUSES = new Set(['À traiter', 'À commander', 'Commandé']);
const NEED_ALLOWED_TRANSITIONS = {
  'À traiter': new Set(['À commander', 'Disponible']),
  'À commander': new Set(['À traiter', 'Commandé']),
  'Commandé': new Set(['À commander', 'Réceptionné']),
  'Réceptionné': new Set(['Disponible']),
  'Disponible': new Set(['À traiter', 'Réservé']),
  'Réservé': new Set(['Disponible', 'Traité']),
  'Traité': new Set(),
};
const canonicalNeedStatus = (status) => status === 'Commander' ? 'Commandé' : status;
const canChangeNeedStatus = (need, target) => {
  const current = canonicalNeedStatus(need.status);
  if (target === current) return true;
  if (need.hasConsumption || current === 'Traité' || current === 'Archivé') return false;
  if (need.hasReception && PRE_RECEIPT_STATUSES.has(target)) return false;
  return Boolean(NEED_ALLOWED_TRANSITIONS[current]?.has(target));
};

const emptyNeed = {
  profile: '', stockItemId: '', resource: '', quantity: 1, dueDate: '', status: 'À traiter',
};

const emptyNeedResource = {
  sku: '', name: '', type: 'Matériel', category: '', unit: 'U', thresholdQuantity: 0,
  location: '', owner: '', purchasePrice: 0, sellingPrice: 0, description: '', markToOrder: false,
};

const movementHelp = {
  'Entrée': 'Augmente le stock physique. À utiliser pour un achat, un réassort ou une réception de matériel neuf.',
  'Sortie': 'Diminue le stock physique disponible. À utiliser lorsqu’un article quitte réellement le stock : consommation, remise à une intervention ou affectation définitive.',
  'Réservation': 'Bloque une quantité pour un besoin futur sans modifier le stock physique. La quantité réservée n’est plus disponible pour les autres usages.',
  'Libération': 'Annule tout ou partie d’une réservation. Le stock physique ne change pas et la quantité redevient disponible.',
  'Retour': 'Réintègre dans le stock physique un article précédemment sorti, par exemple après une intervention ou un prêt.',
  'Mise au rebut': 'Retire définitivement une quantité du stock physique parce qu’elle est détériorée, perdue, obsolète ou jetée.',
  'Ajustement': 'Corrige un écart constaté lors d’un inventaire. Saisissez un nombre positif pour ajouter l’écart ou négatif pour le retirer.',
};

const money = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
const number = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

const statusClass = (status) => {
  if (status === 'Rupture') return 'is-danger';
  if (status === 'Stock faible') return 'is-warning';
  if (status === 'Partiellement réservé') return 'is-info';
  if (status === 'Inactif') return 'is-muted';
  return 'is-success';
};

function Stat({ label, value, hint }) {
  return <Paper className="stock-stat"><span className="stock-stat__label">{label}</span><strong>{value}</strong>{hint && <small>{hint}</small>}</Paper>;
}

function StockPage() {
  const [activeTab, setActiveTab] = useState('stock');
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [summary, setSummary] = useState({ references: 0, lowStock: 0, reserved: 0, stockValue: 0, categories: [], locations: [], types: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState('');
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [movementDialog, setMovementDialog] = useState(false);
  const [needDialog, setNeedDialog] = useState(false);
  const [needResourceDialog, setNeedResourceDialog] = useState(false);
  const [needLinkDialog, setNeedLinkDialog] = useState(false);
  const [needLinkStockItemId, setNeedLinkStockItemId] = useState('');
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [needResourceForm, setNeedResourceForm] = useState(emptyNeedResource);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [movementForm, setMovementForm] = useState(emptyMovement);
  const [needForm, setNeedForm] = useState(emptyNeed);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [movementPagination, setMovementPagination] = useState({ page: 0, pageSize: 10 });
  const [needPagination, setNeedPagination] = useState({ page: 0, pageSize: 10 });
  const [stockActionMenu, setStockActionMenu] = useState(null);
  const [needActionMenu, setNeedActionMenu] = useState(null);
  const [showArchivedNeeds, setShowArchivedNeeds] = useState(false);
  const [receptionConfirmNeed, setReceptionConfirmNeed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [nextSummary, nextItems, nextMovements, nextNeeds] = await Promise.all([
        stockApi.getSummary(), stockApi.listItems(), stockApi.listMovements(), stockApi.listNeeds({ includeArchived: true }),
      ]);
      setSummary(nextSummary); setItems(nextItems); setMovements(nextMovements); setNeeds(nextNeeds);
    } catch (e) { setError(e.message || 'Impossible de charger le stock.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => items.filter((item) => {
    const needle = search.trim().toLowerCase();
    const matchesSearch = !needle || [item.sku, item.name, item.type, item.category, item.location, item.owner]
      .some((value) => String(value || '').toLowerCase().includes(needle));
    return matchesSearch
      && (!category || item.category === category)
      && (!location || item.location === location)
      && (!status || item.computedStatus === status)
      && (!alertsOnly || ['Stock faible', 'Rupture'].includes(item.computedStatus));
  }), [items, search, category, location, status, alertsOnly]);

  const archivedNeedsCount = useMemo(() => needs.filter((need) => need.status === 'Archivé').length, [needs]);
  const visibleNeeds = useMemo(() => (
    showArchivedNeeds ? needs : needs.filter((need) => need.status !== 'Archivé')
  ), [needs, showArchivedNeeds]);

  const openNewItem = () => { setEditingItemId(null); setItemForm(emptyItem); setItemDialog(true); };
  const openEditItem = (item) => {
    setEditingItemId(item.id);
    setItemForm({ ...emptyItem, ...item });
    setItemDialog(true);
  };
  const openMovement = (item) => {
    setMovementForm({ ...emptyMovement, stockItemId: item?.id ? String(item.id) : '' });
    setMovementDialog(true);
  };

  const openNewNeed = () => {
    setNeedForm(emptyNeed);
    setNeedDialog(true);
  };

  const saveItem = async () => {
    if (!itemForm.name.trim()) { setError('La désignation est obligatoire.'); return; }
    setSaving(true); setError('');
    try {
      if (editingItemId) await stockApi.updateItem(editingItemId, itemForm);
      else await stockApi.createItem(itemForm);
      setItemDialog(false); setEditingItemId(null); await load();
    }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const deleteItem = async () => {
    if (!editingItemId || !window.confirm('Archiver cette ressource de stock ? Son historique de mouvements sera conservé.')) return;
    setSaving(true); setError('');
    try { await stockApi.deleteItem(editingItemId); setItemDialog(false); setEditingItemId(null); await load(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const saveMovement = async () => {
    if (!movementForm.stockItemId) { setError('Sélectionnez une ressource.'); return; }
    setSaving(true); setError('');
    try {
      await stockApi.createMovement({
        ...movementForm,
        stockItemId: Number(movementForm.stockItemId),
        quantity: Number(movementForm.quantity),
        referenceId: movementForm.referenceId ? Number(movementForm.referenceId) : null,
        unitPrice: movementForm.unitPrice === '' ? null : Number(movementForm.unitPrice),
      });
      setMovementDialog(false); await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };


  const saveNeed = async () => {
    if (!needForm.profile.trim()) { setError('Le profil ou l’origine du besoin est obligatoire.'); return; }
    if (!needForm.stockItemId && !needForm.resource.trim()) { setError('Indiquez ou sélectionnez une ressource.'); return; }
    setSaving(true); setError('');
    try {
      await stockApi.createNeed({
        ...needForm,
        stockItemId: needForm.stockItemId ? Number(needForm.stockItemId) : null,
        quantity: Number(needForm.quantity),
      });
      setNeedDialog(false);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const changeNeedStatus = async (need, nextStatus) => {
    const currentStatus = canonicalNeedStatus(need.status);
    if (!nextStatus || nextStatus === currentStatus) return;
    if (!canChangeNeedStatus(need, nextStatus)) {
      setError(currentStatus === 'Traité' || need.hasConsumption
        ? 'Ce besoin a déjà été traité : sa sortie de stock est définitive. Archivez-le pour le retirer des besoins actifs.'
        : `Transition non autorisée : « ${currentStatus} » → « ${nextStatus} ».`);
      return;
    }
    if (nextStatus === 'Réceptionné') {
      setReceptionConfirmNeed(need);
      return;
    }
    setSaving(true); setError('');
    try {
      if (nextStatus === 'Réservé' && currentStatus !== 'Réservé') {
        await stockApi.reserveNeed(need.id);
      } else {
        await stockApi.updateNeedStatus(need.id, nextStatus);
      }
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const confirmNeedReception = async () => {
    if (!receptionConfirmNeed) return;
    setSaving(true); setError('');
    try {
      await stockApi.receiveNeed(receptionConfirmNeed.id);
      setReceptionConfirmNeed(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };


  const archiveNeed = async (need) => {
    if (!window.confirm(`Archiver le besoin « ${need.profile} » ? Il restera consultable dans les archives.`)) return;
    setSaving(true); setError('');
    try {
      await stockApi.archiveNeed(need.id);
      setNeedActionMenu(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const restoreNeed = async (need) => {
    setSaving(true); setError('');
    try {
      await stockApi.restoreNeed(need.id);
      setNeedActionMenu(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const openNeedLink = (need) => {
    setSelectedNeed(need);
    setNeedLinkStockItemId('');
    setNeedLinkDialog(true);
  };

  const linkNeedToStock = async () => {
    if (!selectedNeed) return;
    if (!needLinkStockItemId) { setError('Sélectionnez une ressource de stock à lier.'); return; }
    setSaving(true); setError('');
    try {
      await stockApi.linkNeedToStockItem(selectedNeed.id, Number(needLinkStockItemId));
      setNeedLinkDialog(false);
      setNeedLinkStockItemId('');
      setSelectedNeed(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const openNeedResource = (need) => {
    setSelectedNeed(need);
    setNeedResourceForm({
      ...emptyNeedResource,
      name: need.resource || '',
      thresholdQuantity: Number(need.quantity || 0),
    });
    setNeedResourceDialog(true);
  };

  const createNeedResource = async () => {
    if (!selectedNeed) return;
    if (!needResourceForm.name.trim()) { setError('La désignation de la ressource est obligatoire.'); return; }
    setSaving(true); setError('');
    try {
      await stockApi.createStockItemFromNeed(selectedNeed.id, needResourceForm);
      setNeedResourceDialog(false);
      setSelectedNeed(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const stockColumns = [
    { field: 'sku', headerName: 'Référence', width: 110 },
    { field: 'name', headerName: 'Désignation', width: 220 },
    { field: 'category', headerName: 'Catégorie', width: 135 },
    { field: 'quantity', headerName: 'Stock', width: 82, renderCell: ({ row }) => `${number.format(row.quantity)} ${row.unit || ''}` },
    { field: 'reserved', headerName: 'Réservé', width: 88, renderCell: ({ row }) => `${number.format(row.reserved)} ${row.unit || ''}` },
    { field: 'available', headerName: 'Disponible', width: 98, renderCell: ({ row }) => <strong>{number.format(row.available)} {row.unit || ''}</strong> },
    { field: 'thresholdQuantity', headerName: 'Seuil', width: 72, renderCell: ({ row }) => number.format(row.thresholdQuantity) },
    { field: 'location', headerName: 'Emplacement', width: 150 },
    { field: 'stockValue', headerName: 'Valeur', width: 112, renderCell: ({ row }) => money.format(row.stockValue || 0) },
    { field: 'computedStatus', headerName: 'Statut', width: 145, renderCell: ({ row }) => <span className={`stock-status ${statusClass(row.computedStatus)}`}>{row.computedStatus}</span> },
    { field: 'actions', headerName: '', width: 58, renderCell: ({ row }) => {
      const anchor = stockActionMenu?.id === row.id ? stockActionMenu.anchor : null;
      return <div className="stock-row-menu">
        <button type="button" className="stock-row-menu__trigger" aria-label={`Actions ${row.name}`} aria-haspopup="menu" aria-expanded={Boolean(anchor)} onClick={(e) => { e.stopPropagation(); setStockActionMenu(anchor ? null : { id: row.id, anchor: e.currentTarget }); }}>⋯</button>
        <Menu open={Boolean(anchor)} anchorEl={anchor} anchorOrigin={{ horizontal: 'right' }} transformOrigin={{ horizontal: 'right' }} onClose={() => setStockActionMenu(null)}>
          <MenuItem onClick={(e) => { e.stopPropagation(); setStockActionMenu(null); openEditItem(row); }}>Modifier</MenuItem>
          <MenuItem onClick={(e) => { e.stopPropagation(); setStockActionMenu(null); openMovement(row); }}>Nouveau mouvement</MenuItem>
        </Menu>
      </div>;
    } },
  ];

  const movementColumns = [
    { field: 'movementDate', headerName: 'Date', width: 118 },
    { field: 'resource', headerName: 'Ressource', width: 220 },
    { field: 'movement', headerName: 'Mouvement', width: 145 },
    { field: 'quantity', headerName: 'Quantité', width: 100 },
    { field: 'unitPrice', headerName: 'Prix unitaire', width: 125, renderCell: ({ value }) => money.format(Number(value || 0)) },
    { field: 'reason', headerName: 'Motif', width: 300 },
    { field: 'referenceType', headerName: 'Lié à', width: 140, renderCell: ({ row }) => row.referenceType ? `${row.referenceType}${row.referenceId ? ` #${row.referenceId}` : ''}` : '—' },
  ];

  const needColumns = [
    { field: 'profile', headerName: 'Profil / besoin', width: 190 },
    { field: 'resource', headerName: 'Ressource', width: 185 },
    { field: 'quantity', headerName: 'Besoin', width: 82, renderCell: ({ row }) => `${number.format(row.quantity)} ${row.StockItem?.unit || ''}`.trim() },
    { field: 'dueDate', headerName: 'Échéance', width: 120 },
    { field: 'status', headerName: 'Statut', width: 170, renderCell: ({ row }) => row.status === 'Archivé' ? (
      <span className="stock-need-status stock-need-status--archived">Archivé</span>
    ) : row.hasConsumption ? (
      <span className="stock-need-status stock-need-status--treated" title={row.lifecycleReconciled ? 'Statut réconcilié automatiquement avec l’historique des mouvements de stock.' : undefined}>
        Traité{row.lifecycleReconciled ? ' *' : ''}
      </span>
    ) : (
      <select
        className="stock-need-status-select"
        value={canonicalNeedStatus(row.status) || 'À traiter'}
        disabled={saving || canonicalNeedStatus(row.status) === 'Traité'}
        aria-label={`Statut du besoin ${row.profile || row.id}`}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => { event.stopPropagation(); changeNeedStatus(row, event.target.value); }}
      >
        {NEED_STATUSES.map((value) => <option key={value} value={value} disabled={!canChangeNeedStatus(row, value)}>{value}</option>)}
      </select>
    ) },
    { field: 'availability', headerName: 'Disponibilité', width: 155, renderCell: ({ row }) => row.StockItem ? `${number.format(Number(row.StockItem.quantity || 0) - Number(row.StockItem.reserved || 0))} ${row.StockItem.unit || ''}` : 'Non lié au stock' },
    { field: 'actions', headerName: '', width: 58, renderCell: ({ row }) => {
      const hasActions = !row.StockItem || row.status === 'Traité' || row.status === 'Archivé';
      if (!hasActions) return null;
      const anchor = needActionMenu?.id === row.id ? needActionMenu.anchor : null;
      return <div className="stock-row-menu">
        <button type="button" className="stock-row-menu__trigger" aria-label={`Actions du besoin ${row.profile}`} aria-haspopup="menu" aria-expanded={Boolean(anchor)} onClick={(e) => { e.stopPropagation(); setNeedActionMenu(anchor ? null : { id: row.id, anchor: e.currentTarget }); }}>⋯</button>
        <Menu open={Boolean(anchor)} anchorEl={anchor} anchorOrigin={{ horizontal: 'right' }} transformOrigin={{ horizontal: 'right' }} onClose={() => setNeedActionMenu(null)}>
          {!row.StockItem && row.status !== 'Archivé' && <MenuItem onClick={(e) => { e.stopPropagation(); setNeedActionMenu(null); openNeedLink(row); }}>Lier à une ressource existante</MenuItem>}
          {!row.StockItem && row.status !== 'Archivé' && <MenuItem onClick={(e) => { e.stopPropagation(); setNeedActionMenu(null); openNeedResource(row); }}>Créer la ressource</MenuItem>}
          {row.status === 'Traité' && <MenuItem onClick={(e) => { e.stopPropagation(); archiveNeed(row); }}>Archiver</MenuItem>}
          {row.status === 'Archivé' && <MenuItem onClick={(e) => { e.stopPropagation(); restoreNeed(row); }}>Restaurer</MenuItem>}
        </Menu>
      </div>;
    } },
  ];
  const updateItemField = (field) => (event) => setItemForm((current) => ({ ...current, [field]: event.target.value }));
  const updateMovementField = (field) => (event) => setMovementForm((current) => ({ ...current, [field]: event.target.value }));
  const updateNeedField = (field) => (event) => setNeedForm((current) => ({ ...current, [field]: event.target.value }));
  const updateNeedResourceField = (field) => (event) => setNeedResourceForm((current) => ({ ...current, [field]: event.target.value }));

  return (
    <Box className="page-container module-page stock-page">
      <div className="stock-heading">
        <div><Typography variant="h4">Stock & matériel</Typography><p>Suivez les ressources, mouvements, réservations et besoins de l’entreprise.</p></div>
        <div className="stock-heading__actions"><Button onClick={() => openMovement()}>Nouveau mouvement</Button><Button variant="primary" onClick={openNewItem}>Ajouter une ressource</Button></div>
      </div>

      {error && <div className="stock-alert"><span>{error}</span><button type="button" onClick={() => setError('')}>×</button></div>}

      <div className="stock-stats-grid">
        <Stat label="Références" value={summary.references || 0} hint="ressources actives" />
        <Stat label="Alertes stock" value={summary.lowStock || 0} hint="seuils faibles ou ruptures" />
        <Stat label="Réservé" value={number.format(summary.reserved || 0)} hint="unités actuellement bloquées" />
        <Stat label="Valeur du stock" value={money.format(summary.stockValue || 0)} hint="au prix d’achat" />
      </div>

      <div className="stock-tabs" role="tablist">
        {[['stock', 'Stock'], ['movements', 'Mouvements'], ['needs', 'Besoins'], ['assets', 'Immobilisations']].map(([key, label]) => (
          <button key={key} type="button" className={activeTab === key ? 'is-active' : ''} onClick={() => setActiveTab(key)}>{label}{key === 'assets' && <span>À venir</span>}</button>
        ))}
      </div>

      {activeTab === 'stock' && <>
        <Paper className="stock-toolbar">
          <Input label="Recherche" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Référence, matériel, emplacement…" />
          <Select label="Catégorie" value={category} onChange={(e) => setCategory(e.target.value)} options={[{ value: '', label: 'Toutes' }, ...(summary.categories || []).map((value) => ({ value, label: value }))]} />
          <Select label="Emplacement" value={location} onChange={(e) => setLocation(e.target.value)} options={[{ value: '', label: 'Tous' }, ...(summary.locations || []).map((value) => ({ value, label: value }))]} />
          <Select label="Statut" value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: '', label: 'Tous' }, ...['Disponible', 'Partiellement réservé', 'Stock faible', 'Rupture', 'Inactif'].map((value) => ({ value, label: value }))]} />
          <label className="stock-alert-filter"><input type="checkbox" checked={alertsOnly} onChange={(e) => setAlertsOnly(e.target.checked)} /> Alertes uniquement</label>
        </Paper>
        <Paper className="stock-table-panel">
          <div className="stock-table-panel__title"><div><strong>Inventaire</strong><small>{filteredItems.length} ressource(s)</small></div></div>
          {loading ? <div className="stock-loading">Chargement du stock…</div> : <DataGrid className="stock-inventory-grid" rows={filteredItems} columns={stockColumns} getRowId={(row) => row.id} onRowClick={({ row }) => openEditItem(row)} pageSizeOptions={[10, 25, 50]} paginationModel={paginationModel} onPaginationModelChange={setPaginationModel} />}
        </Paper>
      </>}

      {activeTab === 'movements' && <Paper className="stock-table-panel"><div className="stock-table-panel__title"><div><strong>Historique des mouvements</strong><small>Entrées, sorties, réservations, retours et ajustements.</small></div></div><DataGrid rows={movements} columns={movementColumns} getRowId={(row) => row.id} pageSizeOptions={[10, 25, 50]} paginationModel={movementPagination} onPaginationModelChange={setMovementPagination} /></Paper>}

      {activeTab === 'needs' && <Paper className="stock-table-panel">
        <div className="stock-table-panel__title"><div><strong>Besoins</strong><small>Préparez, réservez ou déclenchez la création des ressources nécessaires.</small></div><div className="stock-needs-header-actions"><label className="stock-archive-filter"><input type="checkbox" checked={showArchivedNeeds} onChange={(e) => setShowArchivedNeeds(e.target.checked)} /> Afficher les archives{archivedNeedsCount ? ` (${archivedNeedsCount})` : ''}</label><Button variant="primary" onClick={openNewNeed}>Créer un besoin</Button></div></div>
        <div className="stock-needs-workflow"><span><b>1</b> À traiter</span><i>→</i><span><b>2</b> À commander / stock</span><i>→</i><span><b>3</b> Commandé</span><i>→</i><span><b>4</b> Réceptionné</span><i>→</i><span><b>5</b> Disponible</span><i>→</i><span><b>6</b> Réservé</span><i>→</i><span><b>7</b> Traité</span></div>
        <DataGrid className="stock-needs-grid" rows={visibleNeeds} columns={needColumns} getRowId={(row) => row.id} pageSizeOptions={[10, 25]} paginationModel={needPagination} onPaginationModelChange={setNeedPagination} />
      </Paper>}

      {activeTab === 'assets' && <Paper className="stock-assets-placeholder"><Chip label="V1.1 / V2" /><Typography variant="h5">Immobilisations & matériel affecté</Typography><p>Cette section accueillera les actifs individualisés : ordinateurs, écrans, appareils de mesure, mobilier, véhicules et autres équipements avec numéro de série, affectation, garantie, maintenance et cycle de vie.</p><div className="stock-assets-flow"><span>Acquisition</span><b>→</b><span>Disponible</span><b>→</b><span>Affecté</span><b>→</b><span>Maintenance</span><b>→</b><span>Réaffecté / sorti</span></div></Paper>}

      <Dialog open={itemDialog} onClose={() => { setItemDialog(false); setEditingItemId(null); }} loading={saving} title={editingItemId ? 'Modifier la ressource' : 'Ajouter une ressource'} size="large" actions={<><Button variant="tertiary" onClick={() => { setItemDialog(false); setEditingItemId(null); }} disabled={saving}>Annuler</Button>{editingItemId && <Button variant="danger" onClick={deleteItem} disabled={saving}>Archiver</Button>}<Button variant="primary" onClick={saveItem} loading={saving}>{editingItemId ? 'Enregistrer' : 'Ajouter au stock'}</Button></>}>
        <div className="stock-form-grid">
          <Input label="Référence interne" value={itemForm.sku} onChange={updateItemField('sku')} placeholder="MAT-0001" />
          <Input required label="Désignation" value={itemForm.name} onChange={updateItemField('name')} />
          <Select label="Type" value={itemForm.type} onChange={updateItemField('type')} options={['Matériel', 'Consommable', 'Licence', 'Autre'].map((value) => ({ value, label: value }))} />
          <Input label="Catégorie" value={itemForm.category} onChange={updateItemField('category')} placeholder="Informatique, EPI…" />
          <Select label="Unité" value={itemForm.unit} onChange={updateItemField('unit')} options={['U', 'Ens', 'm', 'ml'].map((value) => ({ value, label: value }))} />
          <Input type="number" label={editingItemId ? 'Quantité en stock' : 'Quantité initiale'} value={itemForm.quantity} onChange={editingItemId ? undefined : updateItemField('quantity')} disabled={Boolean(editingItemId)} helperText={editingItemId ? 'Utilisez un mouvement pour modifier la quantité.' : undefined} />
          <Input type="number" label="Seuil d’alerte" value={itemForm.thresholdQuantity} onChange={updateItemField('thresholdQuantity')} />
          <Input label="Emplacement" value={itemForm.location} onChange={updateItemField('location')} placeholder="Bureau, dépôt…" />
          <Input label="Responsable" value={itemForm.owner} onChange={updateItemField('owner')} />
          <Input type="number" step="0.01" label="Prix d’achat HT" value={itemForm.purchasePrice} onChange={updateItemField('purchasePrice')} />
          <Input type="number" step="0.01" label="Prix de vente HT" value={itemForm.sellingPrice} onChange={updateItemField('sellingPrice')} />
          <Input className="stock-form-grid__wide" multiline label="Description" value={itemForm.description} onChange={updateItemField('description')} />
        </div>
      </Dialog>

      <Dialog open={movementDialog} onClose={() => setMovementDialog(false)} onSubmit={saveMovement} submitLabel="Enregistrer le mouvement" loading={saving} title="Nouveau mouvement de stock" size="large">
        <div className="stock-form-grid">
          <Select className="stock-form-grid__wide" label="Ressource" value={movementForm.stockItemId} onChange={updateMovementField('stockItemId')} options={[{ value: '', label: 'Sélectionner une ressource' }, ...items.map((item) => ({ value: String(item.id), label: `${item.sku ? `${item.sku} — ` : ''}${item.name} (${number.format(item.available)} ${item.unit || ''} disponibles)` }))]} />
          <div className="stock-movement-type">
            <Select label="Type de mouvement" value={movementForm.movement} onChange={updateMovementField('movement')} options={['Entrée', 'Sortie', 'Réservation', 'Libération', 'Retour', 'Mise au rebut', 'Ajustement'].map((value) => ({ value, label: value }))} />
            <small className="stock-field-help">{movementHelp[movementForm.movement]}</small>
          </div>
          <Input type="number" step="0.01" label={movementForm.movement === 'Ajustement' ? 'Écart (+ / -)' : 'Quantité'} value={movementForm.quantity} onChange={updateMovementField('quantity')} />
          <Input type="date" label="Date" value={movementForm.movementDate} onChange={updateMovementField('movementDate')} />
          <Input type="number" step="0.01" label="Prix unitaire HT (facultatif)" value={movementForm.unitPrice} onChange={updateMovementField('unitPrice')} />
          <Input label="Type de référence" value={movementForm.referenceType} onChange={updateMovementField('referenceType')} placeholder="Intervention, Projet…" />
          <Input type="number" label="ID de référence" value={movementForm.referenceId} onChange={updateMovementField('referenceId')} />
          <Input className="stock-form-grid__wide" multiline label="Motif" value={movementForm.reason} onChange={updateMovementField('reason')} placeholder="Réassort, affectation à une intervention…" />
        </div>
      </Dialog>


      <Dialog open={needDialog} onClose={() => setNeedDialog(false)} onSubmit={saveNeed} submitLabel="Créer le besoin" loading={saving} title="Nouveau besoin matériel" size="large">
        <div className="stock-form-grid">
          <Input required label="Profil / origine du besoin" value={needForm.profile} onChange={updateNeedField('profile')} placeholder="Future embauche, poste, demande interne…" />
          <Select label="Ressource du stock (facultatif)" value={needForm.stockItemId} onChange={(event) => {
            const stockItemId = event.target.value;
            const selected = items.find((item) => String(item.id) === String(stockItemId));
            setNeedForm((current) => ({ ...current, stockItemId, resource: selected?.name || current.resource }));
          }} options={[{ value: '', label: 'Besoin non lié à une référence existante' }, ...items.map((item) => ({ value: String(item.id), label: `${item.sku ? `${item.sku} — ` : ''}${item.name}` }))]} />
          <Input required label="Ressource demandée" value={needForm.resource} onChange={updateNeedField('resource')} placeholder="PC portable, écran, EPI…" />
          <Input type="number" min="0.01" step="0.01" label="Quantité" value={needForm.quantity} onChange={updateNeedField('quantity')} />
          <Input type="date" label="Échéance" value={needForm.dueDate} onChange={updateNeedField('dueDate')} />
          <div className="stock-need-note"><strong>Statut initial : À traiter.</strong> Le cycle de vie sera ensuite avancé depuis la liste des besoins afin de garantir la cohérence des mouvements de stock.</div>
          <div className="stock-form-grid__wide stock-need-note">Le lien à une référence existante est facultatif : il permet d’afficher immédiatement la disponibilité actuelle. Un besoin peut aussi être créé avant que le matériel n’existe dans le catalogue de stock.</div>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(receptionConfirmNeed)}
        onClose={() => { if (!saving) setReceptionConfirmNeed(null); }}
        onSubmit={confirmNeedReception}
        submitLabel="Confirmer la réception"
        loading={saving}
        title="Confirmer la réception"
        size="medium"
      >
        <div className="stock-reception-confirm">
          <p><strong>Cette action va incrémenter définitivement le stock.</strong></p>
          <p>
            {receptionConfirmNeed ? `${number.format(receptionConfirmNeed.quantity)} ${receptionConfirmNeed.StockItem?.unit || ''} — ${receptionConfirmNeed.resource || receptionConfirmNeed.StockItem?.name || 'Ressource'}` : ''}
          </p>
          <p>Un mouvement d’entrée lié à ce besoin sera créé. Après validation, le besoin ne pourra plus revenir aux statuts « À traiter », « À commander » ou « Commandé ».</p>
          <div className="stock-need-note">Évolution prévue : cette validation pourra accueillir le numéro de bon de livraison et/ou de facture associé à la réception.</div>
        </div>
      </Dialog>

      <Dialog open={needLinkDialog} onClose={() => { if (!saving) { setNeedLinkDialog(false); setNeedLinkStockItemId(''); setSelectedNeed(null); } }} onSubmit={linkNeedToStock} submitLabel="Lier la ressource" loading={saving} title="Lier le besoin au stock existant" size="medium">
        <div className="stock-form-grid">
          <div className="stock-form-grid__wide stock-need-note">
            <strong>{selectedNeed?.resource || selectedNeed?.profile || 'Besoin'}</strong> — {number.format(Number(selectedNeed?.quantity || 0))} demandé(s).
            Cette action crée uniquement le lien avec une référence existante : aucun mouvement de stock n’est enregistré.
          </div>
          <Select
            className="stock-form-grid__wide"
            label="Ressource de stock"
            value={needLinkStockItemId}
            onChange={(event) => setNeedLinkStockItemId(event.target.value)}
            options={[
              { value: '', label: 'Sélectionner une ressource' },
              ...items.map((item) => ({
                value: String(item.id),
                label: `${item.sku ? `${item.sku} — ` : ''}${item.name} — ${number.format(item.available)} ${item.unit || ''} disponible(s)`,
              })),
            ]}
          />
          {needLinkStockItemId && (() => {
            const item = items.find((candidate) => String(candidate.id) === String(needLinkStockItemId));
            if (!item) return null;
            const enough = Number(item.available || 0) >= Number(selectedNeed?.quantity || 0);
            return <div className="stock-form-grid__wide stock-need-note">
              Stock physique : <strong>{number.format(item.quantity)} {item.unit || ''}</strong> · Réservé : <strong>{number.format(item.reserved)} {item.unit || ''}</strong> · Disponible : <strong>{number.format(item.available)} {item.unit || ''}</strong>.
              {enough ? ' La quantité demandée est disponible : le besoin pourra ensuite passer à « Disponible ».' : ' La quantité disponible est insuffisante : le besoin peut être lié maintenant puis orienté vers une commande.'}
            </div>;
          })()}
        </div>
      </Dialog>

      <Dialog open={needResourceDialog} onClose={() => { setNeedResourceDialog(false); setSelectedNeed(null); }} onSubmit={createNeedResource} submitLabel="Créer et lier la ressource" loading={saving} title="Créer une ressource depuis le besoin" size="large">
        <div className="stock-form-grid">
          <Input label="Référence interne" value={needResourceForm.sku} onChange={updateNeedResourceField('sku')} placeholder="MAT-…" />
          <Input required label="Désignation" value={needResourceForm.name} onChange={updateNeedResourceField('name')} />
          <Input label="Type" value={needResourceForm.type} onChange={updateNeedResourceField('type')} />
          <Input label="Catégorie" value={needResourceForm.category} onChange={updateNeedResourceField('category')} />
          <Select label="Unité" value={needResourceForm.unit} onChange={updateNeedResourceField('unit')} options={['U', 'Ens', 'm', 'ml'].map((value) => ({ value, label: value }))} />
          <Input type="number" min="0" step="0.01" label="Seuil d’alerte" value={needResourceForm.thresholdQuantity} onChange={updateNeedResourceField('thresholdQuantity')} />
          <Input label="Emplacement" value={needResourceForm.location} onChange={updateNeedResourceField('location')} />
          <Input label="Responsable" value={needResourceForm.owner} onChange={updateNeedResourceField('owner')} />
          <Input type="number" min="0" step="0.01" label="Prix d’achat HT" value={needResourceForm.purchasePrice} onChange={updateNeedResourceField('purchasePrice')} />
          <Input type="number" min="0" step="0.01" label="Prix de vente HT" value={needResourceForm.sellingPrice} onChange={updateNeedResourceField('sellingPrice')} />
          <Input className="stock-form-grid__wide" multiline label="Description" value={needResourceForm.description} onChange={updateNeedResourceField('description')} />
          <label className="stock-form-grid__wide stock-checkbox-line"><input type="checkbox" checked={needResourceForm.markToOrder} onChange={(e) => setNeedResourceForm((current) => ({ ...current, markToOrder: e.target.checked }))} /> Marquer automatiquement le besoin « À commander »</label>
          <div className="stock-form-grid__wide stock-need-note">La nouvelle référence est créée avec un stock initial de 0 et automatiquement liée à ce besoin. La quantité réelle sera enregistrée lors de la réception via un mouvement d’entrée.</div>
        </div>
      </Dialog>
    </Box>
  );
}

export default StockPage;
